import pick from 'lodash/pick';
import sortBy from 'lodash/sortBy';

import {DISABLED_DUE_TO_CLASSIFIED_DATA} from 'common/report/constants';
import {
  getTooltipDataForCohort,
  getTooltipDataForEvent,
  getTooltipDataForProperty,
} from 'common/widgets/context-panel/util';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {BuilderPaneBase} from 'common/widgets/cohorts-query-builder/common-components/builder-pane-base/index';
import {
  ANY_EVENT,
  SHOW_MORE_LIMIT,
  TABS_LIMIT_NORMAL_WIDTH,
} from 'common/widgets/cohorts-query-builder/models/constants';
import {PredefinedCohort, Property} from 'common/widgets/cohorts-query-builder/models/enums/expression-enums';
import {ConditionTab, ConditionType, Type} from 'common/widgets/cohorts-query-builder/models/enums/builder-enums';
import {
  createDataGroupTabData,
  createPrimitiveTabData,
  displayNameForBehaviorEvent,
  getIconForEvent,
  getIconForPropertyType,
  joinKeyPropertyFromStagedDimension,
  renameProperty,
  sorted,
} from 'common/widgets/cohorts-query-builder/util/index';
import {
  isCohortLiteral,
  isEventLiteral,
  isPeopleLiteral,
} from 'common/widgets/cohorts-query-builder/util/staged-cohort';

import template from './index.jade';
import css from './index.styl?inline';

function getCohortItemFromCohort({cohort, selectedCohortId}) {
  return {
    conditionType: ConditionType.Cohorts,
    id: cohort.id,
    label: cohort.name,
    isSelected: selectedCohortId === cohort.id,
  };
}

class ConditionBuilderCondition extends BuilderPaneBase {
  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      'pane-visible': `boolean`,
      'user-id': `number`,
    };
  }

  shouldUpdate(state) {
    return !!(
      state.stagedCohort &&
      state.stagedCohort.literals[state.stagedClausePosition.literalIdx] &&
      this.builderState.conditionTab
    );
  }

  get config() {
    return {
      ...super.config,
      useShadowDom: true,
      css,
      template,
      helpers: {
        ...super.config.helpers,
        pick,
        ConditionTab,
        canUseCohorts: () => {
          return this.app.canUseCohorts();
        },
        displayWideScreen: () => this.helpers.getConditionTabs().length > TABS_LIMIT_NORMAL_WIDTH,
        hasDataGroups: () => this.app.hasDataGroups(),
        // TODO opslead
        // eslint-disable-next-line sonarjs/cognitive-complexity
        getConditionTabs: () => {
          const {conditionTabType, stagedDimension} = this.builderState;
          if (stagedDimension) {
            return [createPrimitiveTabData(stagedDimension.conditionType)];
          }

          const isAnalyzingUser = this.app.isAnalyzingUser();
          const canShowUserProps = isAnalyzingUser && this.state.projectHasPeople;
          const canShowCohorts = this.app.canUseCohorts() && (isAnalyzingUser || this.app.hasB2BCohorts());
          const {dataGroups} = this.state;
          if (this.state.stagedClausePosition.clauseIdx === 0) {
            return [
              ...(this.app.hasMultipleConditionTypes() ? [createPrimitiveTabData(ConditionTab.All)] : []),
              createPrimitiveTabData(ConditionTab.Events),
              ...(canShowUserProps ? [createPrimitiveTabData(ConditionTab.People)] : []),
              ...(dataGroups ? dataGroups.map((dataGroup) => createDataGroupTabData(dataGroup)) : []),
              ...(canShowCohorts ? [createPrimitiveTabData(ConditionTab.Cohorts)] : []),
            ];
          } else {
            if (conditionTabType === ConditionTab.Events) {
              return [createPrimitiveTabData(ConditionTab.Events)];
            }
            return [
              ...(canShowUserProps ? [createPrimitiveTabData(ConditionTab.People)] : []),
              ...(dataGroups ? dataGroups.map((dataGroup) => createDataGroupTabData(dataGroup)) : []),
            ];
          }
        },
        getSections: () => {
          const {conditionTabType, conditionTab} = this.builderState;
          if (conditionTabType === ConditionTab.All) {
            return [
              this.getAllUsersSection(),
              ...this.helpers
                .getConditionTabs()
                .filter((tab) => tab.type !== ConditionTab.All)
                .map((tab) => this.getTabSections(tab.type, tab.value, false))
                .flat(),
            ];
          }
          return [...this.getTabSections(conditionTabType, conditionTab)];
        },
        getPaneTitle: () => {
          switch (this.builderState.conditionTabType) {
            case ConditionTab.All:
              return `Select an item`;
            case ConditionTab.Events:
              return `Select an event`;
            case ConditionTab.People:
              return `Select a profile property`;
            case ConditionTab.Cohorts:
              return `Select a cohort`;
            case ConditionTab.DataGroup:
              return `Select a ${this.getSelectedDataGroup(this.builderState.conditionTab).displayName} property`;
          }
        },
        shouldShowToggle: () => {
          return this.helpers.getConditionTabs().length > 1;
        },
        clickedItem: (ev) => {
          const {item} = ev.detail;
          const isDimension = item.type === Type.Dimension;

          if (isDimension) {
            this.updateBuilder({searchFilter: ``, stagedDimension: item});
          } else {
            this.updateBuilder({stagedDimension: null});
          }

          switch (item.conditionType) {
            case ConditionType.Events:
              this.dispatchBubbledEvent(`selected-event`, item);
              break;
            case ConditionType.People: {
              const {
                name,
                propertyType,
                joinPropertyType,
                dimensionDataGroupId,
                dataGroupId,
                customPropertyId,
                type,
              } = item;

              this.dispatchBubbledEvent(`selected-property`, {
                name,
                type,
                propertyType,
                joinPropertyType,
                dimensionDataGroupId: customPropertyId ? item.customProperty.mappedDataGroupId : dimensionDataGroupId,
                dataGroupId,
                customPropertyId,
              });

              if (isDimension) {
                this.nextPane(`cohorts-builder-condition`);
              } else {
                this.nextPane(`cohorts-builder-pane-filter-property`);
              }
              break;
            }
            case ConditionType.Cohorts:
              this.dispatchBubbledEvent(`selected-cohort`, pick(item, [`id`]));
              break;
          }
        },
        showItemDetails: (ev) => this.showItemDetails(ev.detail.item),
        handleConditionTabToggleChange: (ev) => {
          const selectedTab = this.helpers.getConditionTabs().find((tab) => tab.value === ev.detail.selected);
          this.updateBuilder({conditionTab: ev.detail.selected, conditionTabType: selectedTab.type});
        },
      },
    };
  }

  showItemDetails(item) {
    let tooltipData;
    switch (item.conditionType) {
      case ConditionType.Events: {
        const mpEvent = pick(item, [`id`, `name`, `custom`, `is_collect_everything_event`]);
        tooltipData = getTooltipDataForEvent(mpEvent, this.state.eventDefinitions);
        break;
      }
      case ConditionType.People: {
        if (!item.dataGroupId) {
          const mpProperty = pick(item, [`name`, `customPropertyId`, `customProperty`, `isDisabled`]);
          if (mpProperty.customPropertyId || mpProperty.customProperty) {
            mpProperty.actionDisabled = true;
          }
          tooltipData = !item.isDisabled
            ? getTooltipDataForProperty(mpProperty, this.state.userPropertyDefinitions, {
                lookupTables: this.state.lookupTables,
              })
            : null;
        }
        break;
      }
      case ConditionType.Cohorts: {
        const cohort = this.app.getCohortById(item.id, {includePredefined: false});
        tooltipData = cohort ? getTooltipDataForCohort(cohort) : null;
        break;
      }
    }
    this.update({tooltipData});
  }

  eventListItem(ev, selectedEvent) {
    return {
      conditionType: ConditionType.Events,
      custom: ev.custom,
      icon: getIconForEvent(ev),
      id: ev.id,
      isSelected: ev.name === selectedEvent.name && ev.custom === selectedEvent.custom,
      label: displayNameForBehaviorEvent(ev.name),
      ...ev,
    };
  }

  getDataGroupSections(selectedDataGroup) {
    if (!selectedDataGroup || !selectedDataGroup.properties) {
      return [];
    }
    const dataGroupId = selectedDataGroup.groupID;
    const items = selectedDataGroup.properties.map((property) => ({
      conditionType: ConditionType.People,
      propertyType: Property.User,
      dataGroupId,
      label: renameProperty(property.name, {dataGroupId}),
      icon: getIconForPropertyType(property.type),
      hasCaret: true,
      isDisabled: property.sensitive,
      isSelected: false,
      tooltip: property.sensitive ? {text: DISABLED_DUE_TO_CLASSIFIED_DATA} : null,
      ...property,
    }));
    return [
      {
        label: selectedDataGroup.displayName,
        items,
        showLimit: SHOW_MORE_LIMIT,
      },
    ];
  }

  getSelectedDataGroup(dataGroupId) {
    return this.state.dataGroups.find((dataGroup) => dataGroup.groupID === dataGroupId);
  }

  getPeoplePropertiesSections() {
    const {stagedCohort, stagedClausePosition, peopleProperties} = this.state;
    const selectedPropertyName = isPeopleLiteral(this.stagedLiteral)
      ? stagedCohort.getPropertyName(stagedClausePosition)
      : ``;
    const items = peopleProperties.map((ev) => ({
      conditionType: ConditionType.People,
      propertyType: Property.User,
      label: renameProperty(ev.name),
      icon: ev.name === `$mp_first_event_time` ? `star-top-events` : getIconForPropertyType(ev.type),
      hasCaret: true,
      isDisabled: ev.sensitive,
      isSelected: ev.name === selectedPropertyName,
      tooltip: ev.sensitive ? {text: DISABLED_DUE_TO_CLASSIFIED_DATA} : null,
      ...ev,
    }));

    return [
      {
        label: `Profile Properties`,
        items,
        showLimit: SHOW_MORE_LIMIT,
      },
    ];
  }

  getEventsSections() {
    const {stagedCohort, stagedClausePosition, topEvents, customEvents} = this.state;
    const defaultEvents = [ANY_EVENT];
    const event = isEventLiteral(this.stagedLiteral) ? stagedCohort.getBehavior(stagedClausePosition).getEvent() : ``;
    const selectedEvent = {
      custom: event.startsWith(`$custom_event:`),
      name: displayNameForBehaviorEvent(event, customEvents),
    };
    const items = [
      ...defaultEvents.map((eventName) =>
        this.eventListItem(
          {
            name: eventName,
            icon: `star-top-events`,
          },
          selectedEvent,
        ),
      ),
      ...sorted(
        [...customEvents, ...topEvents].map((ev) => this.eventListItem(ev, selectedEvent)),
        {
          transform: (ev) => ev.label.toLowerCase(),
        },
      ),
    ];

    return [
      {
        label: `All Events`,
        items,
        showLimit: SHOW_MORE_LIMIT,
      },
    ];
  }

  getSelectedCohortId() {
    const {stagedCohort, stagedClausePosition} = this.state;
    return isCohortLiteral(this.stagedLiteral) ? stagedCohort.getCohortId(stagedClausePosition) : null;
  }

  getAllUsersSection() {
    return {
      items: [
        getCohortItemFromCohort({
          cohort: this.app.getCohortById(PredefinedCohort.AllUsers),
          selectedCohortId: this.getSelectedCohortId(),
        }),
      ],
      showLimit: SHOW_MORE_LIMIT,
    };
  }

  _isYourCohort(cohort) {
    return cohort.createdBy.id === this.attr(`user-id`);
  }

  _cohortSection(label, cohortFilter) {
    return {
      label,
      items: sortBy(Object.values(this.state.cohorts), [`name`])
        .filter(cohortFilter)
        .map((cohort) => getCohortItemFromCohort({cohort, selectedCohortId: this.getSelectedCohortId()})),
      showLimit: SHOW_MORE_LIMIT,
    };
  }

  getDimensionSections() {
    const stagedDimension = this.builderState.stagedDimension;

    const joinKeyItem = joinKeyPropertyFromStagedDimension(stagedDimension);
    const properties = [joinKeyItem, ...(this.state.dimensionProperties || [])];

    return [
      {
        isLoading: this.state.loading.dimensions,
        items: properties.map((item) => ({
          conditionType: stagedDimension.conditionType,
          joinPropertyType: stagedDimension.joinPropertyType,
          propertyType: stagedDimension.propertyType,
          label: renameProperty(item.name),
          icon: getIconForPropertyType(item.type),
          hasCaret: true,
          ...item,
        })),
        showLimit: SHOW_MORE_LIMIT,
      },
    ];
  }

  getCohortsSections(splitCohortsSections = false) {
    if (splitCohortsSections) {
      return [
        this._cohortSection(`Your Cohorts`, (cohort) => this._isYourCohort(cohort)),
        this._cohortSection(`All Cohorts`, (cohort) => !this._isYourCohort(cohort) && cohort.can_view),
      ];
    }
    return [this._cohortSection(`Cohorts`, (cohort) => cohort.can_view)];
  }

  getTabSections(type, value, splitCohortsSections = true) {
    const {stagedDimension} = this.builderState;
    if (stagedDimension && stagedDimension.conditionType === type) {
      return this.getDimensionSections();
    }

    switch (type) {
      case ConditionTab.Events:
        return this.getEventsSections();
      case ConditionTab.People:
        return this.getPeoplePropertiesSections();
      case ConditionTab.Cohorts:
        // don't want to split cohorts into "Yours", "All", when it's the all tab
        return this.getCohortsSections(splitCohortsSections);
      case ConditionTab.DataGroup:
        return this.getDataGroupSections(this.getSelectedDataGroup(value));
    }
  }

  get stagedLiteral() {
    const {stagedCohort, stagedClausePosition} = this.state;
    return stagedCohort.literals[stagedClausePosition.literalIdx];
  }

  get hasStagedEventLiteral() {
    return isEventLiteral(this.stagedLiteral);
  }

  get hasStagedCohortLiteral() {
    return isCohortLiteral(this.stagedLiteral);
  }

  get hasStagePeopleLiteral() {
    return !this.hasStagedCohortLiteral && !this.hasStagedEventLiteral;
  }
}

export const ConditionBuilderConditionEl = defineAndMakeJsxFactory(
  `cohorts-builder-condition`,
  ConditionBuilderCondition,
);
